import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlobUrl } from '@/hooks/use-blob-url';
import { saveBlob } from '@/lib/save-blob';
import {
  debtResource,
  type AddPaymentInput,
  type CreateDebtInput,
  type DebtFileInput,
  type DebtFileRef,
  type DebtRef,
  type PaymentRef,
  type UpdateDebtInput,
  type UpdatePaymentInput,
} from './debt.resource';

export const debtsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'debts'],
    queryFn: () => debtResource.list(organizationId),
  });

export function useDebts(organizationId: string | null) {
  return useQuery({
    ...debtsQuery(organizationId ?? ''),
    enabled: Boolean(organizationId),
    select: (data) => data.debts,
  });
}

export function useDebt(organizationId: string, debtId: string) {
  return useQuery({
    ...debtsQuery(organizationId),
    select: (data) => data.debts.find((debt) => debt.id === debtId),
  });
}

function useRefresh() {
  const client = useQueryClient();
  return (organizationId: string) =>
    client.invalidateQueries({ queryKey: ['organizations', organizationId, 'debts'] });
}

export function useCreateDebt() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateDebtInput) => debtResource.create(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useUpdateDebt() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: UpdateDebtInput) => debtResource.update(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useDeleteDebt() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: DebtRef) => debtResource.remove(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useNudgeDebt() {
  return useMutation({
    mutationFn: (ref: DebtRef) => debtResource.nudge(ref),
  });
}

export function useAddPayment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: AddPaymentInput) => debtResource.addPayment(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useUpdatePayment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: UpdatePaymentInput) => debtResource.updatePayment(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useRemovePayment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: PaymentRef) => debtResource.removePayment(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useRemoveReceipt() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: PaymentRef) => debtResource.removeReceipt(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useAddDebtFile() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: DebtFileInput) => debtResource.addFile(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useRemoveDebtFile() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: DebtFileRef) => debtResource.removeFile(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useDownloadReceipt() {
  return useMutation({
    mutationFn: async (ref: PaymentRef & { filename: string }) => {
      saveBlob(await debtResource.getReceipt(ref), ref.filename);
    },
  });
}

export function useReceiptUrl(ref: PaymentRef) {
  return useBlobUrl(`${ref.debtId}/${ref.paymentId}`, () => debtResource.getReceipt(ref));
}

export const debtFileLoader =
  (ref: DebtRef) =>
  (file: { id: string }): Promise<Blob> =>
    debtResource.getFile({ ...ref, attachmentId: file.id });
